import "./instruments";
import "../css/app.css";

import { createInertiaApp, router } from "@inertiajs/react";
import { configureEcho } from "@laravel/echo-react";
import { reactErrorHandler } from "@sentry/react";
import { resolvePageComponent } from "laravel-vite-plugin/inertia-helpers";
import { createRoot } from "react-dom/client";
import type { SharedDataConfig } from "@/types";
import { isChromium } from "@/utils/browser";
import { ErrorBoundary } from "./components/error-boundaries/error-boundary";
import { initializeTheme } from "./hooks/use-appearance";
import { setupAxiosInterceptors } from "./lib/axios-setup";
import { LocalizationProvider } from "./lib/providers/localization-provider";

setupAxiosInterceptors();

configureEcho({
  broadcaster: "reverb",
  key: import.meta.env.VITE_REVERB_APP_KEY,
  wsHost: import.meta.env.VITE_REVERB_HOST,
  wsPort: import.meta.env.VITE_REVERB_PORT,
  wssPort: import.meta.env.VITE_REVERB_PORT,
  forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? "https") === "https",
  enabledTransports: ["ws", "wss"],
});

const appName = import.meta.env.VITE_APP_NAME || "Nila Health";

createInertiaApp({
  title: (title) => (title ? `${title} - ${appName}` : appName),
  resolve: (name) =>
    resolvePageComponent(
      `./pages/${name}.tsx`,
      import.meta.glob("./pages/**/*.tsx")
    ),
  setup({ el, App, props }) {
    const root = createRoot(el, {
      onUncaughtError: reactErrorHandler((error, errorInfo) => {
        // biome-ignore lint/suspicious/noConsole: Development logging
        console.warn("Uncaught error", error, errorInfo.componentStack);
      }),
      onCaughtError: reactErrorHandler(),
      onRenderError: reactErrorHandler(),
    });

    const config = props.initialPage.props.config as SharedDataConfig;

    root.render(
      <LocalizationProvider localization={config.localization}>
        <ErrorBoundary>
          <App {...props} />
        </ErrorBoundary>
      </LocalizationProvider>
    );
  },
  progress: {
    color: "#4B5563",
  },
});

// This will set light / dark mode on load...
initializeTheme();

// Send browser info header on all Inertia requests
router.on("before", (event) => {
  event.detail.visit.headers["X-Is-Chromium"] = String(isChromium());
});
